import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  PhoneCall,
  MessageCircle,
  Mail,
  Facebook,
  Globe,
  MapPin,
} from 'lucide-react';
import { useT } from '../i18n';
import { useKiooAudio } from '../AudioProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const digitsOnly = (v) => (v || '').replace(/[^\d]/g, '');

const CountryBlock = ({ country }) => {
  if (!country) return null;
  return (
    <div
      data-testid={`mobile-contact-country-${country.code}`}
      className="rounded-2xl bg-white border border-emerald-100 shadow-sm overflow-hidden"
    >
      <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
        <span className="text-xl leading-none">{country.flag}</span>
        <div className="font-bold text-emerald-900 leading-tight">
          {country.name}
        </div>
      </div>
      <div className="p-3 space-y-2">
        {(country.phones || []).map((p) => (
          <a
            key={p}
            href={`tel:${p}`}
            data-testid={`mobile-contact-call-${country.code}-${digitsOnly(p)}`}
            className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-700 to-emerald-600 text-white font-semibold active:scale-[0.98] transition-transform"
          >
            <PhoneCall className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{p}</span>
          </a>
        ))}
        {country.whatsapp && (
          <a
            href={`https://wa.me/${digitsOnly(country.whatsapp)}`}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`mobile-contact-whatsapp-${country.code}`}
            className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#25D366] text-white font-semibold active:scale-[0.98] transition-transform"
          >
            <MessageCircle className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">WhatsApp {country.whatsapp}</span>
          </a>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, href, testid }) => {
  if (!value) return null;
  const Wrap = href ? 'a' : 'div';
  const props = href
    ? {
        href,
        target: href.startsWith('http') ? '_blank' : undefined,
        rel: href.startsWith('http') ? 'noreferrer noopener' : undefined,
      }
    : {};
  return (
    <Wrap
      {...props}
      data-testid={testid}
      className="flex items-center gap-3 rounded-2xl p-4 shadow-sm bg-white border border-gray-100 active:scale-[0.98] transition-transform"
    >
      <div className="w-11 h-11 rounded-full flex items-center justify-center bg-emerald-100 flex-shrink-0">
        <Icon className="w-5 h-5 text-emerald-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
          {label}
        </div>
        <div className="font-semibold text-gray-900 truncate">{value}</div>
      </div>
    </Wrap>
  );
};

const MobileContact = () => {
  const t = useT();
  const { currentStation } = useKiooAudio();
  const stationSlug = currentStation?.slug || 'kioo';
  const [c, setC] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setC(null);
    setErr('');
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/mobile/contact?station=${encodeURIComponent(stationSlug)}`
        );
        if (alive) setC(data);
      } catch (_e) {
        if (alive) setErr('Could not load contact info.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [stationSlug]);

  if (err) {
    return (
      <div className="px-4 pt-6">
        <div
          data-testid="mobile-contact-error"
          className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800"
        >
          {err}
        </div>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="px-4 pt-8 text-center text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-4 space-y-3" data-testid="mobile-contact-page">
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">
          {t('contact.title')}
        </h1>
        <p className="text-xs text-gray-500 mt-0.5">
          {c.station_name} · {c.tagline}
        </p>
      </div>

      {/* Coming Soon placeholder — used for stations without published contacts
          (e.g. Radio Kilikezu). */}
      {c.coming_soon && (
        <div
          data-testid="mobile-contact-coming-soon"
          className="rounded-2xl bg-white border-2 p-6 text-center shadow-sm"
          style={{ borderColor: 'var(--kioo-primary)' }}
        >
          <div className="text-4xl">📻</div>
          <div
            className="mt-2 text-2xl font-extrabold"
            style={{ color: 'var(--kioo-primary-dark)' }}
          >
            {c.coming_soon_message || 'Coming Soon!'}
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Contact details for {c.station_name} will be published here.
          </p>
        </div>
      )}

      {/* Multi-country phone / WhatsApp cards */}
      {Array.isArray(c.countries) && c.countries.length > 0 && (
        <div className="space-y-3">
          {c.countries.map((country) => (
            <CountryBlock key={country.code} country={country} />
          ))}
        </div>
      )}

      <InfoRow
        icon={Mail}
        label={t('contact.email')}
        value={c.email}
        href={c.email ? `mailto:${c.email}` : null}
        testid="mobile-contact-email"
      />
      <InfoRow
        icon={Facebook}
        label={t('contact.facebook')}
        value={c.facebook}
        href={c.facebook}
        testid="mobile-contact-facebook"
      />
      <InfoRow
        icon={Globe}
        label={t('contact.website')}
        value={c.website}
        href={c.website}
        testid="mobile-contact-website"
      />
      <InfoRow
        icon={MapPin}
        label={t('contact.studio')}
        value={c.studio_address}
        testid="mobile-contact-studio"
      />

      {c.studio_region && (
        <div className="text-center text-xs text-gray-500 mt-2 italic">
          {c.studio_region}
        </div>
      )}
    </div>
  );
};

export default MobileContact;
